import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Paper,
  Grid,
  Radio,
  FormControlLabel,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  FormControl,
  Select,
  Collapse,
  Tooltip,
} from "@mui/material";
import { DoubleArrow, MoreVert } from "@mui/icons-material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ModeStandbyIcon from "@mui/icons-material/ModeStandby";
import CommentIcon from "@mui/icons-material/Comment";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { LoaderWithoutHeight } from "components/Loader";
import { UserDetailsRow } from "components/CustomComponents/UserDetailsRow";
import { themes, infoStyle } from "services/constants";
import { useFetch } from "services/hooks/useFetch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ExclamatryiconIcon from "@mui/icons-material/ErrorOutline";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { tooltipStyleProps } from "services/constants/Styles";

// Helper to build timeline history robustly
export const buildStatusHistory = (data) => {
  if (!data) return [];
  const history = [];

  if (data.applied_at || data.request_date) {
    history.push({
      status: "Applied",
      user: data.applied_by || data.user_name || "User",
      date: data.applied_at || data.request_date,
      color: "#00ab66",
    });
  }

  if (data.status === "Rejected") {
    history.push({
      status: "Pre-Approval: Rejected",
      user: data.rejected_by || data.approver_name || "Admin",
      date: data.rejected_at || "N/A",
      color: "#ff0000",
    });
    if (data.rejection_reason) {
      history.push({
        status: "Reason",
        reason: data.rejection_reason,
        isReason: true,
        color: "#d3d3d3",
      });
    }
  }

  if (data.status === "Approved" || data.status === "Active") {
    history.push({
      status: data.status === "Active" ? "Activated" : "Approved",
      user: data.approved_by || data.approver_name || "Admin",
      date: data.approved_at || "N/A",
      color: "#00ab66",
    });
  }

  return history;
};

// Fixed Custom Timeline Component (No double dots, clean start/stop connector)
export const CustomTimeline = ({ history }) => (
  <Box sx={{ mt: 1, ml: 1 }}>
    {history.map((item, index) => {
      const isLast = index === history.length - 1;
      return (
        <Box
          key={index}
          sx={{ position: "relative", pl: 3, pb: isLast ? 0 : 3 }}
        >
          {/* Connector Line (Only render if it's NOT the last item) */}
          {!isLast && (
            <Box
              sx={{
                position: "absolute",
                left: "4px", // Centers exactly under the 10px dot
                top: "14px",
                bottom: "-2px",
                width: "2px",
                bgcolor: "#eee",
              }}
            />
          )}
          {/* Timeline Dot Badge */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 5,
              width: 10, // Reduced badge size to match design
              height: 10,
              borderRadius: "50%",
              bgcolor: item.color || "#ccc",
              zIndex: 1,
            }}
          />
          {item.isReason ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <strong>{item.status}:</strong> {item.reason}
            </Typography>
          ) : (
            <Typography variant="body2">
              <strong>{item.status}</strong>{" "}
              {item.user ? `by ${item.user}` : ""} On {item.date}
            </Typography>
          )}
        </Box>
      );
    })}
  </Box>
);

const InfoCard = ({ label, value, icon: Icon, color }) => (
  <Box
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      bgcolor: color,
      border: "1px solid #eee",
    }}
  >
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ mb: 1, opacity: 0.7 }}
    >
      {Icon && <Icon sx={{ fontSize: 18 }} />}
      <Typography variant="caption" fontWeight="bold">
        {label}
      </Typography>
    </Stack>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
);

const SummaryCard = ({ title, amount, icon: Icon, color, lightColor }) => (
  <Paper
    variant="outlined"
    sx={{ p: 2, borderRadius: 2, borderColor: color, flex: 1 }}
  >
    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
      <Box
        sx={{
          p: 0.5,
          borderRadius: 1,
          bgcolor: lightColor,
          color: color,
          display: "flex",
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Typography variant="caption" color="text.secondary" fontWeight="bold">
        {title}
      </Typography>
    </Stack>
    <Typography variant="h5" fontWeight="bold">
      {amount}
    </Typography>
  </Paper>
);

const AdvanceSalaryPanel = ({
  onClose,
  reCallApi,
  mode = "create",
  initialData = null,
  onEdit,
}) => {
  const { domain, userDetails, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );

  const isView = mode === "view";
  const isEdit = mode === "edit";
  const isDetailedView =
    isView &&
    (initialData?.status === "Approved" ||
      initialData.status === "Pre-Closed" ||
      initialData.status === "Partially-Paid" ||
      initialData.status === "Closed" ||
      initialData?.status === "Active");

  const [detailedData, setDetailedData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [formData, setFormData] = useState({
    amount: "",
    reason: "",
    repayment_mode: "Full",
    repayment_date: dayjs()
      .add(1, "month")
      .startOf("month")
      .format("YYYY-MM-DD"),
    no_of_installments: 1,
  });

  // Skip request states
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRepaymentRow, setSelectedRepaymentRow] = useState(null);
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [skipPayload, setSkipPayload] = useState({
    skip_payrun_id: "",
    reason: "",
  });

  const [expandedRow, setExpandedRow] = useState(null);

  // Payruns for skip dialog
  const payrunUrl = topbarCompanyId
    ? `${domain}payroll/operations/payrollprocessing/payrollruncycle/payschedules/?status=Upcoming&company_id=${topbarCompanyId}`
    : "";
  const { result: payruns = [] } = useFetch(payrunUrl);

  // Function to fetch details - abstracted so it can be called after skips
  const fetchDetails = async () => {
    if (!initialData?.id) return;
    setLoadingDetails(true);
    try {
      const res = await customGetAPI(
        `${domain}advance/request/${initialData.id}/`,
      );
      // Extract the nested 'data' object reliably
      const extractedData = res.responseDetails || res.data || res;
      setDetailedData(extractedData);
    } catch (err) {
      console.error("Failed to fetch advance details", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Fetch detailed data properly mapped
  useEffect(() => {
    if (isDetailedView) {
      fetchDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetailedView, initialData?.id, domain]);

  useEffect(() => {
    if ((isView || isEdit) && initialData) {
      const repaymentDate =
        initialData.repayment_date || initialData.repayment_start_date || "";
      // Ensure date is in YYYY-MM-DD format for date input
      const formattedDate = repaymentDate
        ? dayjs(repaymentDate, "DD-MM-YYYY").format("YYYY-MM-DD")
        : "";

      setFormData({
        amount: initialData.amount || "",
        reason: initialData.description || initialData.reason || "",
        repayment_mode: initialData.repayment_mode || "Full",
        effective_repayment_date: formattedDate,
        no_of_installments:
          initialData.no_of_installments ||
          initialData.number_of_installments ||
          1,
      });
    }
  }, [isView, isEdit, initialData]);

  // Handle Double nested 'data' scenarios if API response dictates
  const displayData = detailedData?.data || detailedData || initialData || {};
  const timelineHistory = buildStatusHistory(displayData);

  const isInstallment = formData.repayment_mode?.startsWith("Installment");
  const monthlyAmount =
    isInstallment && formData.amount && formData.no_of_installments > 0
      ? (
          parseFloat(formData.amount) / parseInt(formData.no_of_installments)
        ).toFixed(2)
      : 0;

  const handleChange = (field, value) => {
    // Validate date fields - prevent past dates
    if (field === "effective_repayment_date" && value) {
      const selectedDate = dayjs(value, "YYYY-MM-DD");
      const today = dayjs().startOf("day");
      if (selectedDate.isBefore(today)) {
        showToast("error", "Please select a future date for repayment.");
        return;
      }
    }

    setFormData((prev) => {
      const newState = { ...prev, [field]: value };
      if (field === "repayment_mode" && value === "Full") {
        newState.no_of_installments = 1;
      }
      return newState;
    });
  };

  const isSubmitDisabled = () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) return true;
    if (!formData.reason?.trim()) return true;
    if (!formData.effective_repayment_date) return true;
    if (
      isInstallment &&
      (!formData.no_of_installments || formData.no_of_installments < 1)
    )
      return true;
    return false;
  };

  const handleSubmit = async () => {
    setActionLoading(true);
    const payload = {
      amount: parseFloat(formData.amount),
      reason: formData.reason,
      repayment_mode: formData.repayment_mode,
      repayment_date: formData.effective_repayment_date,
      no_of_installments: isInstallment
        ? parseInt(formData.no_of_installments)
        : 1,
      user_id: userDetails?.id,
    };

    try {
      const endpoint = isEdit
        ? `${domain}advance/request/${initialData.id}/`
        : `${domain}advance/request/`;
      const method = isEdit ? "PUT" : "POST";
      const res = await customAPI(endpoint, method, payload);

      if (res.ok) {
        showToast(
          "success",
          `Advance request ${isEdit ? "updated" : "submitted"} successfully`,
        );
        if (reCallApi) reCallApi(true);
        if (onClose) onClose();
      } else {
        showToast("error", res.message || "Failed to submit request");
      }
    } catch (error) {
      console.error("Advance Submit Error:", error);
      showToast("error", "Something went wrong.");
    } finally {
      setActionLoading(false);
    }
  };

  // Skip request handlers
  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRepaymentRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openSkipModal = () => {
    setSkipPayload({ skip_payrun_id: "", reason: "" });
    setSkipModalOpen(true);
    handleMenuClose();
  };

  const handleSkipDialogClose = (event, reason) => {
    if (reason === "backdropClick") return;
    setSkipModalOpen(false);
  };

  const handleSkipSubmit = async () => {
    if (!skipPayload.skip_payrun_id || !skipPayload.reason) {
      showToast("error", "Please provide a reason and select a payrun.");
      return;
    }

    setActionLoading(true);
    try {
      const payload = {
        user_id: userDetails.id,
        skip_payrun_id: skipPayload.skip_payrun_id,
        reason: skipPayload.reason,
      };

      const res = await customAPI(
        `${domain}advance/repayment/${selectedRepaymentRow.id}/skip/`,
        "POST",
        payload,
      );

      if (res.ok) {
        showToast("success", "Skip requested successfully.");
        setSkipModalOpen(false);
        fetchDetails(); // Refresh details to update the table status
      } else {
        showToast("error", res.message || "Failed to submit skip request.");
      }
    } catch (error) {
      showToast("error", "Error requesting skip.");
    } finally {
      setActionLoading(false);
    }
  };

  const renderDetailedView = () => (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Box
          sx={{
            border: "1px solid #00ab66",
            color: "#00ab66",
            borderRadius: 4,
            px: 2,
            py: 0.5,
            fontSize: "0.8rem",
            fontWeight: "bold",
          }}
        >
          {displayData.state}
        </Box>
      </Box>

      <Stack direction="row" spacing={2} mb={3}>
        <SummaryCard
          title="Advance Amount"
          amount={displayData.approved_amount || displayData.amount || "-"}
          icon={ShoppingBagOutlinedIcon}
          color="#2ecc71"
          lightColor="#eafaf1"
        />
        <SummaryCard
          title="Paid Amount"
          amount={displayData.paid_amount || "0.00"}
          icon={AccountBalanceWalletOutlinedIcon}
          color="#e91e63"
          lightColor="#fce4ec"
        />
        <SummaryCard
          title="Balance"
          amount={displayData.balance_amount || "-"}
          icon={CardGiftcardOutlinedIcon}
          color="#3f51b5"
          lightColor="#e8eaf6"
        />
      </Stack>

      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={2}>
          {[
            { label: "Request date", value: displayData.request_date },
            { label: "Amount", value: displayData.effective_amount },
            { label: "Reason", value: displayData.reason },
            {
              label: "Disbursement date",
              value: displayData.disbursement_date || "-",
            },
            { label: "Repayment Mode", value: displayData.repayment_mode },
            {
              label: "No of Instalement",
              value:
                displayData.approved_installments ||
                displayData.no_of_installments,
            },
          ].map((item, i) => (
            <React.Fragment key={i}>
              <Grid item xs={5}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {item.label}
                </Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography variant="body2" color="text.secondary">
                  {item.value || "-"}
                </Typography>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Paper>

      <Typography variant="subtitle1" fontWeight="bold" mb={2}>
        Repayment History
      </Typography>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2 }}
      >
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Due Date</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Due</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Paid Amount</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Balance</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Payrun</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Maps successfully now since we properly extracted inner data */}
            {displayData.repayment_schedule?.length > 0 ? (
              displayData.repayment_schedule.map((row, index) => (
                <React.Fragment key={index}>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: "nowrap", width: "80px" }}>
                      {row.due_date || "-"}
                    </TableCell>
                    <TableCell>{row.amount_due || "-"}</TableCell>
                    <TableCell>{row.amount_paid || "-"}</TableCell>
                    <TableCell>{row.balance || "-"}</TableCell>

                    <TableCell>
                      <Tooltip
                        title={row.payrun_label || "N/A"}
                        slotProps={tooltipStyleProps}
                      >
                        {row.payrun || "N/A"}
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {row.skip_status !== null &&
                      row.skip_status !== undefined ? (
                        <Box
                          sx={{
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                          onClick={() => {
                            setExpandedRow((prev) =>
                              prev === index ? null : index,
                            );
                          }}
                        >
                          {row.status || "-"}
                          <InfoOutlinedIcon
                            sx={{ fontSize: 14, opacity: 0.7 }}
                          />
                        </Box>
                      ) : (
                        row.status || "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, row)}
                        disabled={
                          row.status === "Skipped" ||
                          row.status === "Paid" ||
                          row.status === "Skip-Requested"
                        }
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {row.skip_status !== null &&
                    row.skip_status !== undefined && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{ p: 0, bgcolor: "action.hover" }}
                        >
                          <Collapse
                            in={expandedRow === index}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ p: 2, bgcolor: "#fafbfc" }}>
                              <Paper
                                elevation={0}
                                sx={{
                                  position: "relative",
                                  borderRadius: 2,
                                  border: "1px solid #eaebec",
                                  overflow: "hidden",
                                  bgcolor: "#fff",
                                }}
                              >
                                {/* Yellow Left Accent Border */}
                                <Box
                                  sx={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    bgcolor: "#f5a623",
                                  }}
                                />

                                {/* Header */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    p: 2,
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Typography fontSize={20}>🪙</Typography>
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight={700}
                                      sx={{
                                        borderBottom: "1px solid #ccc",
                                        pb: 0.2,
                                      }}
                                    >
                                      Skip Details
                                    </Typography>
                                  </Stack>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setExpandedRow(null)}
                                    sx={{
                                      color: "#333",
                                      borderColor: "#d3d3d3",
                                      textTransform: "none",
                                      borderRadius: 2,
                                    }}
                                  >
                                    Hide
                                  </Button>
                                </Box>

                                {/* Detail Cards */}
                                <Grid
                                  container
                                  spacing={2}
                                  sx={{ px: 2, alignItems: "stretch" }}
                                >
                                  {/* Status Card */}
                                  <Grid item xs={12} md={4}>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        height: "100%",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          bgcolor: "#e6f4ea",
                                          borderRadius: "50%",
                                          p: 0.8,
                                          display: "flex",
                                        }}
                                      >
                                        {row.skip_status?.toLowerCase() ===
                                        "approved" ? (
                                          <CheckCircleIcon
                                            sx={{
                                              color: "#34a853",
                                              fontSize: 20,
                                            }}
                                          />
                                        ) : row.skip_status?.toLowerCase() ===
                                          "rejected" ? (
                                          <CancelIcon
                                            sx={{
                                              color: "#f44336",
                                              fontSize: 20,
                                            }}
                                          />
                                        ) : (
                                          <ExclamatryiconIcon
                                            sx={{
                                              color: "#131312",
                                              fontSize: 20,
                                            }}
                                          />
                                        )}
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          fontWeight={600}
                                        >
                                          Skip Status
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              bgcolor:
                                                row.skip_status?.toLowerCase() ===
                                                "approved"
                                                  ? "#e6f4ea"
                                                  : row.skip_status?.toLowerCase() ===
                                                      "rejected"
                                                    ? "#fdecea"
                                                    : "#f3ded8",
                                              color:
                                                row.skip_status?.toLowerCase() ===
                                                "approved"
                                                  ? "#137333"
                                                  : row.skip_status?.toLowerCase() ===
                                                      "rejected"
                                                    ? "#f84913"
                                                    : "#131312",
                                              px: 1,
                                              py: 0.5,
                                              borderRadius: 1,
                                              fontWeight: 600,
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                            }}
                                          >
                                            {row.skip_status?.toLowerCase() ===
                                              "approved" && (
                                              <CheckCircleIcon
                                                sx={{ fontSize: 14 }}
                                              />
                                            )}
                                            {row.skip_status?.toLowerCase() ===
                                              "rejected" && (
                                              <CancelIcon
                                                sx={{ fontSize: 14 }}
                                              />
                                            )}
                                            {row.skip_status?.toLowerCase() ===
                                              "pending" && (
                                              <ExclamatryiconIcon
                                                sx={{ fontSize: 14 }}
                                              />
                                            )}
                                            {row.skip_status || "Pending"}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Paper>
                                  </Grid>

                                  {/* Reason Card */}
                                  <Grid item xs={12} md={4}>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 2,
                                        height: "100%",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          bgcolor: "#e8f0fe",
                                          borderRadius: "50%",
                                          minWidth: 34,
                                          height: 34,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Typography fontSize={16}>
                                          📋
                                        </Typography>
                                      </Box>
                                      <Box sx={{ width: "100%" }}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          fontWeight={600}
                                        >
                                          Skip Reason
                                        </Typography>
                                        <Tooltip
                                          title={row.skip_reason || "N/A"}
                                          slotProps={tooltipStyleProps}
                                          arrow
                                        >
                                          <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            sx={{
                                              mt: 0.5,
                                              display: "-webkit-box",
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            {row.skip_reason || "N/A"}
                                          </Typography>
                                        </Tooltip>
                                      </Box>
                                    </Paper>
                                  </Grid>

                                  {/* Remark Card */}
                                  <Grid item xs={12} md={4}>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 2,
                                        height: "100%",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          bgcolor: "#f1f3f4",
                                          borderRadius: "50%",
                                          minWidth: 34,
                                          height: 34,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Typography
                                          fontSize={16}
                                          fontWeight="bold"
                                          color="#5f6368"
                                        >
                                          ❝
                                        </Typography>
                                      </Box>
                                      <Box sx={{ width: "100%" }}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          fontWeight={600}
                                        >
                                          Skip Remark
                                        </Typography>
                                        <Tooltip
                                          title={
                                            row.skip_review_remarks || "N/A"
                                          }
                                          slotProps={tooltipStyleProps}
                                          arrow
                                        >
                                          <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            sx={{
                                              mt: 0.5,
                                              display: "-webkit-box",
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            {row.skip_review_remarks || "N/A"}
                                          </Typography>
                                        </Tooltip>
                                      </Box>
                                    </Paper>
                                  </Grid>
                                </Grid>

                                {/* Footer Details */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    p: 2,
                                    mt: 1,
                                    gap: { xs: 2, md: 4 },
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                  >
                                    <CalendarTodayIcon
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: 20,
                                      }}
                                    />
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        fontWeight={600}
                                      >
                                        Requested On
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        color="#333"
                                      >
                                        {row.skip_requested_on || "-"}
                                      </Typography>
                                    </Box>
                                  </Stack>

                                  <Box
                                    sx={{
                                      width: "1px",
                                      height: "30px",
                                      bgcolor: "#e0e0e0",
                                    }}
                                  />

                                  <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                  >
                                    <PersonOutlineOutlinedIcon
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: 22,
                                      }}
                                    />
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        fontWeight={600}
                                      >
                                        {row.skip_status === "Approved" ||
                                        row.skip_status === "Pending"
                                          ? "Approved By"
                                          : "Rejected By"}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        color="#333"
                                      >
                                        {row.skip_approved_by || "-"}
                                      </Typography>
                                    </Box>
                                  </Stack>

                                  <Box
                                    sx={{
                                      width: "1px",
                                      height: "30px",
                                      bgcolor: "#e0e0e0",
                                      display: { xs: "none", md: "block" },
                                    }}
                                  />

                                  <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                  >
                                    <EventAvailableOutlinedIcon
                                      sx={{
                                        color: "text.secondary",
                                        fontSize: 22,
                                      }}
                                    />
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        fontWeight={600}
                                      >
                                        {row.skip_status === "Approved" ||
                                        row.skip_status === "Pending"
                                          ? "Approved On"
                                          : "Rejected On"}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        color="#333"
                                      >
                                        {row.skip_approved_on || "-"}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </Box>
                              </Paper>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  No repayment schedule available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu for the Repayment Table */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ elevation: 2, sx: { minWidth: 120 } }}
      >
        {selectedRepaymentRow?.status === "Pending" && (
          <MenuItem onClick={openSkipModal} sx={{ fontSize: "0.9rem" }}>
            Skip Request
          </MenuItem>
        )}
      </Menu>
    </Box>
  );

  // Skip modal
  const renderSkipModal = () => (
    <Dialog
      open={skipModalOpen}
      onClose={handleSkipDialogClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Skip Advance Repayment
        </Typography>
        <IconButton onClick={() => setSkipModalOpen(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderBottom: "none" }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Skip Payrun
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={skipPayload.skip_payrun_id}
              onChange={(e) =>
                setSkipPayload({
                  ...skipPayload,
                  skip_payrun_id: e.target.value,
                })
              }
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Payrun
              </MenuItem>
              {payruns.map((payrun) => (
                <MenuItem key={payrun.id} value={payrun.id}>
                  {payrun.name || payrun.label || `Payrun ${payrun.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Reason
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter Reason"
            value={skipPayload.reason}
            onChange={(e) =>
              setSkipPayload({ ...skipPayload, reason: e.target.value })
            }
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Button
          onClick={() => setSkipModalOpen(false)}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSkipSubmit}
          variant="contained"
          disabled={actionLoading}
          sx={{ bgcolor: "#00ab66", "&:hover": { bgcolor: "#008f55" } }}
        >
          {actionLoading ? "Processing..." : "Skip"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#fff",
        width: "100%",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", px: 2, pt: 2 }}>
          <IconButton
            onClick={onClose}
            sx={{ color: themes?.grayColor, mr: 1, ml: -1 }}
          >
            <DoubleArrow />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {isView
                ? isDetailedView
                  ? "Advance"
                  : "View Advance Request"
                : isEdit
                  ? "Edit Advance Request"
                  : "Advance Request"}
            </Typography>
            <Typography {...infoStyle}>
              {isView
                ? "View your payoff request."
                : "Submit an advance request and choose a repayment option."}
            </Typography>
          </Box>
          {isView &&
            onEdit &&
            !isDetailedView &&
            initialData?.status !== "Rejected" && (
              <IconButton
                onClick={onEdit}
                size="small"
                sx={{ bgcolor: "#e8f5e9", color: "#00ab66" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
        </Box>
      </Paper>

      <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <UserDetailsRow
            userDetails={{
              image_url: userDetails?.image_url,
              employee_id: displayData?.employee_id || userDetails?.employee_id,
              user_name: displayData?.user_name || userDetails?.first_name,
              department_name:
                displayData?.department || userDetails?.department_name,
              designation: displayData?.designation || userDetails?.designation,
            }}
          />
        </Box>

        {isView ? (
          loadingDetails ? (
            <LoaderWithoutHeight info="Loading details..." />
          ) : isDetailedView ? (
            renderDetailedView()
          ) : (
            <Box>
              <InfoCard
                label="Amount"
                value={formData.amount}
                icon={AccountBalanceWalletOutlinedIcon}
                color="#f0f7ff"
              />
              {/* Fix: Only displays the dates based on the mode correctly */}
              {formData.repayment_mode === "Full" ? (
                <InfoCard
                  label="Repayment Date"
                  value={dayjs(formData.effective_repayment_date).format(
                    "DD.MM.YYYY",
                  )}
                  icon={CalendarTodayIcon}
                  color="#d6e6e3"
                />
              ) : (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <InfoCard
                      label="No. of Installments"
                      value={formData.no_of_installments}
                      icon={HelpOutlineIcon}
                      color="#fffcf0"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <InfoCard
                      label="Monthly Amount"
                      value={monthlyAmount}
                      icon={AccountBalanceWalletIcon}
                      color="#fffcf0"
                    />
                  </Box>
                </Box>
              )}

              {/* always show mode */}
              <InfoCard
                label="Repayment Mode"
                value={formData.repayment_mode}
                icon={ModeStandbyIcon}
                color="#fff0f6"
              />

              {/* show start date separately when using installment */}
              {isInstallment && (
                <InfoCard
                  label="Repayment Start Date"
                  value={dayjs(formData.effective_repayment_date).format(
                    "DD.MM.YYYY",
                  )}
                  icon={CalendarTodayIcon}
                  color="#e2f3f0"
                />
              )}
              <InfoCard
                label="Reason"
                value={formData.reason}
                icon={CommentIcon}
                color="#e9e6f7"
              />

              {timelineHistory.length > 0 && (
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    Status History
                  </Typography>
                  <CustomTimeline history={timelineHistory} />
                </Grid>
              )}
            </Box>
          )
        ) : (
          <Stack spacing={3}>
            {/* Same Create/Edit Fields as Before */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Request date
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                This date is used for approval and payroll processing.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#00ab66", fontWeight: "bold", mt: 0.5 }}
              >
                {dayjs().format("DD.MM.YYYY")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Amount
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Enter the advance amount you wish to request.
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={formData.amount}
                onChange={(e) => handleChange("amount", e.target.value)}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Reason
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Mention the reason for requesting the advance. This will help in
                faster approval.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                value={formData.reason}
                onChange={(e) => handleChange("reason", e.target.value)}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Repayment mode
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Choose the repayment mode for the advance.
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    onClick={() => handleChange("repayment_mode", "Full")}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      borderRadius: 2,
                      border:
                        formData.repayment_mode === "Full"
                          ? "2px solid #00ab66"
                          : "1px solid #ddd",
                      bgcolor:
                        formData.repayment_mode === "Full"
                          ? "#f0fff4"
                          : "transparent",
                    }}
                  >
                    <FormControlLabel
                      value="Full"
                      control={
                        <Radio
                          checked={formData.repayment_mode === "Full"}
                          size="small"
                          sx={{ color: "#00ab66" }}
                        />
                      }
                      label={
                        <Typography variant="subtitle2" fontWeight="bold">
                          Full
                        </Typography>
                      }
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ ml: 3.5 }}
                    >
                      One-time deduction in the next applicable payroll after
                      approval.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    onClick={() =>
                      handleChange("repayment_mode", "Installment")
                    }
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      borderRadius: 2,
                      border:
                        formData.repayment_mode === "Installment"
                          ? "2px solid #00ab66"
                          : "1px solid #ddd",
                      bgcolor:
                        formData.repayment_mode === "Installment"
                          ? "#f0fff4"
                          : "transparent",
                    }}
                  >
                    <FormControlLabel
                      value="Installment"
                      control={
                        <Radio
                          checked={formData.repayment_mode === "Installment"}
                          size="small"
                          sx={{ color: "#00ab66" }}
                        />
                      }
                      label={
                        <Typography variant="subtitle2" fontWeight="bold">
                          Installment
                        </Typography>
                      }
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ ml: 3.5 }}
                    >
                      The advance amount will be split into instalments and
                      deducted monthly.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {isInstallment && (
              <Box>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      No. of Installments
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ minHeight: 40, mb: 1 }}
                    >
                      Number of payroll cycles over which the advance will be
                      repaid.
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formData.no_of_installments}
                      onChange={(e) =>
                        handleChange("no_of_installments", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Monthly Repayment Amount
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ minHeight: 40, mb: 1 }}
                    >
                      Amount to be deducted from each payroll cycle.
                    </Typography>
                    <Box
                      sx={{
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: "#00ab66", fontWeight: "bold" }}
                      >
                        {monthlyAmount}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
                {isInstallment ? "Repayment Start Date" : "Repayment date"}
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={formData.effective_repayment_date}
                onChange={(e) =>
                  handleChange("effective_repayment_date", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: dayjs().format("YYYY-MM-DD"),
                }}
              />
            </Box>
          </Stack>
        )}
      </Box>

      {!isView && (
        <Box sx={{ p: 2, borderTop: "1px solid #eee" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitDisabled() || actionLoading}
              sx={{ bgcolor: "#84deb8", px: 4 }}
            >
              {actionLoading ? "Processing..." : isEdit ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      )}
      {renderSkipModal()}
    </Box>
  );
};

export default AdvanceSalaryPanel;
